import { useState, useEffect } from "react";
import Loading from "../loading";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";
import "../../assets/fonts.css";
import Navigation from "../Navigation";
import Notification from "../Notification";
import {
  createNewNotice,
  getAllNotices,
  deleteNotice,
  getNoticeReaders,
} from "../../api/notice";

const Notice = () => {
  // ==========================================
  // Stateها
  // ==========================================
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [status, setStatus] = useState();
  const [refresh, setRefresh] = useState(true);

  // فرم اطلاعیه
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [targetAudience, setTargetAudience] = useState("all");

  // لیست اطلاعیه‌ها
  const [noticeList, setNoticeList] = useState([]);

  // مودال خوانندگان
  const [readersModal, setReadersModal] = useState(false);
  const [readersData, setReadersData] = useState(null);
  const [readersLoading, setReadersLoading] = useState(false);

  // ==========================================
  // ارسال فرم
  // ==========================================
  const handleSubmit = async () => {
    // اعتبارسنجی
    if (subject.trim() === "") {
      alert("لطفاً موضوع را وارد کنید");
      return;
    }
    if (text.trim() === "") {
      alert("لطفاً متن اطلاعیه را وارد کنید");
      return;
    }

    setLoading(true);

    const data = {
      subject: subject.trim(),
      text: text.trim(),
      targetAudience,
    };

    const res = await createNewNotice(data);
    setStatus(res.status);
    setShowNotification(true);

    // بازنشانی فرم در صورت موفقیت
    if (res.status === 201) {
      setSubject("");
      setText("");
      setTargetAudience("all");
      await fetchNotices();
    }

    setLoading(false);
  };

  // ==========================================
  // حذف اطلاعیه
  // ==========================================
  const deleteNot = async (id) => {
    if (!window.confirm("آیا از حذف این اطلاعیه مطمئن هستید؟")) return;

    const res = await deleteNotice(id);
    if (res?.status === 200) {
      setNoticeList(noticeList.filter((n) => n._id !== id));
    }
  };

  // ==========================================
  // دریافت لیست خوانندگان
  // ==========================================
  const showReaders = async (id) => {
    setReadersModal(true);
    setReadersLoading(true);
    setReadersData(null);

    try {
      const res = await getNoticeReaders(id);
      if (res?.status === 200) {
        setReadersData(res.data);
      }
    } catch (err) {
      console.error("Error fetching readers:", err);
    } finally {
      setReadersLoading(false);
    }
  };

  // ==========================================
  // دریافت اطلاعیه‌ها
  // ==========================================
  const fetchNotices = async () => {
    try {
      const res = await getAllNotices();
      if (res?.data?.notices) {
        setNoticeList(res.data.notices);
      }
    } catch (err) {
      console.error("Error fetching notices:", err);
    }
  };

  useEffect(() => {
    fetchNotices();
  }, []);

  // ==========================================
  // دریافت اطلاعات ادمین
  // ==========================================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [admin] = await Promise.all([GetAdmin()]);
        setAdminData(admin);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refresh]);

  // ==========================================
  // Loading و دسترسی
  // ==========================================
  if (loading) return <Loading />;
  if (!adminData) return null;

  // ==========================================
  // Notification props
  // ==========================================
  const getNotificationProps = () => {
    switch (status) {
      case 201:
        return {
          message: "ساخت اعلان موفقیت آمیز بود.",
          type: "success",
          duration: 3000,
        };
      case 400:
        return {
          message: "اطلاعات ارسالی نامعتبر است.",
          type: "error",
          duration: 3000,
        };
      case 404:
        return {
          message: "کاربری با اطلاعات شما پیدا نشد دوباره ورود کنید.",
          type: "error",
          duration: 3000,
        };
      case 500:
        return {
          message: "مشکل سرور به برنامه نویس اطلاع دهید.",
          type: "error",
          duration: 3000,
        };
      default:
        return null;
    }
  };

  const notificationProps = getNotificationProps();

  // ==========================================
  // برچسب مخاطب
  // ==========================================
  const getAudienceLabel = (audience) => {
    switch (audience) {
      case "all":
        return { text: "همه کاربران", color: "bg-green-100 text-green-700" };
      case "user":
        return { text: "کاربران عادی", color: "bg-blue-100 text-blue-700" };
      case "non-user":
        return { text: "کاربران ویژه", color: "bg-purple-100 text-purple-700" };
      default:
        return { text: "نامشخص", color: "bg-gray-100 text-gray-700" };
    }
  };

  // ==========================================
  // بررسی دسترسی
  // ==========================================
  if (adminData.role === "manager" || adminData.role === "questionMaker") {
    return (
      <>
        <div className="w-full flex justify-center flex-col items-center">
          {showNotification && (
            <Notification
              {...notificationProps}
              setShowNotification={setShowNotification}
              showNotification={showNotification}
            />
          )}

          <div className="flex flex-col md:flex-row gap-6 justify-between w-full max-w-7xl px-4">
            {/* نوار ناوبری */}
            <div className="w-full md:w-1/4 mb-6 lg:mb-0">
              <Navigation />
            </div>

            {/* فرم ایجاد اطلاعیه */}
            <div className="w-full md:w-3/4">
              <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md rtl">
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
                  ایجاد اطلاعیه جدید
                </h2>

                {/* موضوع */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    موضوع:
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="موضوع اطلاعیه را وارد کنید..."
                    maxLength={200}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-left">
                    {subject.length} / 200
                  </p>
                </div>

                {/* متن اطلاعیه */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    متن اطلاعیه:
                  </label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="متن اطلاعیه را وارد کنید..."
                    rows={4}
                    maxLength={1000}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-left">
                    {text.length} / 1000
                  </p>
                </div>

                {/* مخاطب اطلاعیه */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    مخاطب اطلاعیه:
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: "all", label: "همه کاربران" },
                      { value: "user", label: "کاربران عادی" },
                      { value: "non-user", label: "کاربران ویژه" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setTargetAudience(option.value)}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${
                          targetAudience === option.value
                            ? "bg-blue-500 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* دکمه ارسال */}
                <button
                  type="button"
                  className="w-full py-3 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors font-medium"
                  onClick={handleSubmit}
                >
                  ارسال اطلاعیه
                </button>
              </div>
            </div>
          </div>

          {/* ==========================================
              جدول اطلاعیه‌ها
          ========================================== */}
          <div className="w-full mx-auto mt-10 p-6 bg-white rounded-lg shadow-md rtl">
            <h3 className="text-xl font-bold mb-4 text-gray-800">
              لیست اطلاعیه‌های ثبت‌شده
            </h3>

            {noticeList.length === 0 ? (
              <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-md">
                هیچ اطلاعیه‌ای موجود نیست
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-gray-700">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700 text-center">
                      <th className="py-3 px-4">موضوع</th>
                      <th className="py-3 px-4">متن</th>
                      <th className="py-3 px-4">مخاطب</th>
                      <th className="py-3 px-4">تعداد خوانندگان</th>
                      <th className="py-3 px-4">تاریخ</th>
                      <th className="py-3 px-4">عملیات</th>
                    </tr>
                  </thead>

                  <tbody>
                    {noticeList.map((notice) => {
                      const audience = getAudienceLabel(
                        notice.targetAudience || "all",
                      );
                      return (
                        <tr
                          key={notice._id}
                          className="border-b hover:bg-gray-50 text-center"
                        >
                          {/* موضوع */}
                          <td className="py-3 px-4 font-semibold text-gray-800 text-right">
                            {notice.subject}
                          </td>

                          {/* متن */}
                          <td className="py-3 px-4 text-gray-600 text-right max-w-md">
                            {notice.text}
                          </td>

                          {/* مخاطب */}
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-xl text-xs font-medium ${audience.color}`}
                            >
                              {audience.text}
                            </span>
                          </td>

                          {/* تعداد خوانندگان */}
                          <td className="py-3 px-4">
                            <button
                              onClick={() => showReaders(notice._id)}
                              className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-200 transition"
                            >
                              👁 {notice.readCount || 0} نفر
                            </button>
                          </td>

                          {/* تاریخ */}
                          <td className="py-3 px-4">
                            {new Date(notice.created_at).toLocaleDateString(
                              "fa-IR",
                            )}
                          </td>

                          {/* عملیات */}
                          <td className="py-3 px-4">
                            <button
                              onClick={() => deleteNot(notice._id)}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                            >
                              حذف
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ==========================================
            مودال لیست خوانندگان
        ========================================== */}
        {readersModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setReadersModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* هدر مودال */}
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                <h3 className="text-lg font-bold text-gray-800">
                  خوانندگان اطلاعیه
                </h3>
                <button
                  onClick={() => setReadersModal(false)}
                  className="text-gray-500 hover:text-gray-800 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* محتوای مودال */}
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                {readersLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                  </div>
                ) : !readersData || readersData.readers?.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    هنوز کسی این اطلاعیه را ندیده است
                  </p>
                ) : (
                  <>
                    {/* خلاصه اطلاعیه */}
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        موضوع:{" "}
                        <span className="font-bold">
                          {readersData.notice?.subject}
                        </span>
                      </p>
                      <p className="text-sm text-blue-800 mt-1">
                        متن:{" "}
                        <span className="font-bold">
                          {readersData.notice?.text}
                        </span>
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        تعداد کل خوانندگان:{" "}
                        <span className="font-bold">
                          {readersData.totalReaders}
                        </span>
                      </p>
                    </div>

                    {/* لیست خوانندگان */}
                    <ul className="space-y-2">
                      {readersData.readers.map((reader, index) => (
                        <li
                          key={reader._id || index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-sm">
                              {reader.userId?.name?.[0] || "?"}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {reader.userId?.name || "کاربر ناشناس"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {reader.userId?.phone || "—"} •{" "}
                                {reader.userId?.role || "—"}
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400">
                            {new Date(reader.seenAt).toLocaleDateString(
                              "fa-IR",
                            )}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return <NotFoundPage />;
};

export default Notice;
